import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Cpu, HardDrive, Network, MemoryStick, Server, AlertCircle, CheckCircle2, XCircle, Settings, RefreshCw, TrendingUp, TrendingDown, Bell, Clock, Filter, Search, Eye, EyeOff, Edit, Save, Plus, Trash2, Info, HelpCircle, ExternalLink, Copy, Globe, Database, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Zap, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Users, Activity, BarChart3, PieChart, LineChart, ScrollText
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const ResourceManagement = () => {
  const [resourceMetrics, setResourceMetrics] = useState(null);
  const [resourceAllocations, setResourceAllocations] = useState([]);
  const [resourceUsage, setResourceUsage] = useState(null);
  const [resourcePools, setResourcePools] = useState([]);
  const [resourceConfig, setResourceConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadResourceData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metricsResponse = await AdminService.getResourceMetrics(timeRange);
      const allocationsResponse = await AdminService.getResourceAllocations();
      const usageResponse = await AdminService.getResourceUsage(timeRange);
      const poolsResponse = await AdminService.getResourcePools();
      const configResponse = await AdminService.getResourceConfig();

      if (metricsResponse.success && allocationsResponse.success && usageResponse.success && poolsResponse.success && configResponse.success) {
        setResourceMetrics(metricsResponse.data);
        setResourceAllocations(allocationsResponse.data);
        setResourceUsage(usageResponse.data);
        setResourcePools(poolsResponse.data);
        setResourceConfig(configResponse.data);
      } else {
        setError(metricsResponse.message || allocationsResponse.message || usageResponse.message || poolsResponse.message || configResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch resource management data.');
      console.error('Error fetching resource management data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadResourceData();
    const interval = setInterval(loadResourceData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [loadResourceData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'allocated': return 'text-blue-600 bg-blue-100';
      case 'reserved': return 'text-yellow-600 bg-yellow-100';
      case 'overallocated': return 'text-red-600 bg-red-100';
      case 'maintenance': return 'text-orange-600 bg-orange-100';
      case 'offline': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp size={16} className="text-red-500" />;
    if (trend === 'down') return <TrendingDown size={16} className="text-green-500" />;
    return null;
  };

  const handleAllocateResource = async (resourceId) => {
    if (window.confirm('Are you sure you want to allocate this resource?')) {
      try {
        const response = await AdminService.allocateResource(resourceId);
        if (response.success) {
          alert('Resource allocated successfully!');
          loadResourceData();
        } else {
          alert(`Failed to allocate resource: ${response.message}`);
        }
      } catch (err) {
        alert('Error allocating resource.');
        console.error('Error allocating resource:', err);
      }
    }
  };

  const handleDeallocateResource = async (resourceId) => {
    if (window.confirm('Are you sure you want to deallocate this resource?')) {
      try {
        const response = await AdminService.deallocateResource(resourceId);
        if (response.success) {
          alert('Resource deallocated successfully!');
          loadResourceData();
        } else {
          alert(`Failed to deallocate resource: ${response.message}`);
        }
      } catch (err) {
        alert('Error deallocating resource.');
        console.error('Error deallocating resource:', err);
      }
    }
  };

  const handleUpdateResourceConfig = async () => {
    if (window.confirm('Are you sure you want to update resource configuration?')) {
      try {
        const response = await AdminService.updateResourceConfig(resourceConfig);
        if (response.success) {
          alert('Resource configuration updated successfully!');
          loadResourceData();
        } else {
          alert(`Failed to update configuration: ${response.message}`);
        }
      } catch (err) {
        alert('Error updating configuration.');
        console.error('Error updating configuration:', err);
      }
    }
  };

  const handleCreateResourcePool = async () => {
    const poolName = prompt('Enter name for new resource pool:');
    if (poolName) {
      try {
        const response = await AdminService.createResourcePool({ name: poolName });
        if (response.success) {
          alert(`Resource pool "${poolName}" created successfully!`);
          loadResourceData();
        } else {
          alert(`Failed to create resource pool: ${response.message}`);
        }
      } catch (err) {
        alert('Error creating resource pool.');
        console.error('Error creating resource pool:', err);
      }
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading resource management data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!resourceMetrics || !resourceUsage || !resourceConfig) {
    return <div className="p-6 text-center text-gray-500">No resource management data available.</div>;
  }

  const filteredAllocations = resourceAllocations.filter(allocation => {
    const matchesFilter = resourceFilter === 'all' || allocation.resourceType === resourceFilter;
    const matchesSearch = searchTerm === '' ||
      allocation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allocation.resourceType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allocation.status.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Server className="mr-3 text-indigo-600" size={30} />
          Resource Management
        </h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="1h">Last 1 Hour</option>
            <option value="6h">Last 6 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <button
            onClick={handleCreateResourcePool}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <Plus className="mr-2" size={18} /> Create Pool
          </button>
          <button
            onClick={loadResourceData}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Monitor and manage system resources, allocations, and pools to ensure optimal resource utilization.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(resourceMetrics.lastUpdated).toLocaleString()}</span>
      </p>

      {/* Resource Overview Metrics */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <LayoutDashboard className="mr-2 text-purple-600" size={24} /> Resource Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {resourceMetrics.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(metric.status)}`}>
                    {metric.status}
                  </span>
                  {metric.trend && <span className="ml-3">{getTrendIcon(metric.trend)}</span>}
                </div>
              </div>
              {metric.type === 'cpu_resources' && <Cpu size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'memory_resources' && <MemoryStick size={40} className="text-green-500 opacity-70" />}
              {metric.type === 'storage_resources' && <HardDrive size={40} className="text-orange-500 opacity-70" />}
              {metric.type === 'network_resources' && <Network size={40} className="text-purple-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* Resource Usage Analysis */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <BarChart3 className="mr-2 text-blue-600" size={24} /> Resource Usage Analysis
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <Cpu className="mr-2 text-blue-500" size={20} /> CPU Resource Allocation
            </h3>
            {/* Placeholder for CPU Resource Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <PieChart size={30} className="mr-2" /> CPU Resource Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.cpuResources.map((resource, index) => (
                <li key={index} className="flex justify-between">
                  <span>{resource.name}:</span>
                  <span className="font-medium text-gray-800">{resource.allocation}%</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <MemoryStick className="mr-2 text-green-500" size={20} /> Memory Resource Allocation
            </h3>
            {/* Placeholder for Memory Resource Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <BarChart3 size={30} className="mr-2" /> Memory Resource Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.memoryResources.map((resource, index) => (
                <li key={index} className="flex justify-between">
                  <span>{resource.name}:</span>
                  <span className="font-medium text-gray-800">{resource.allocation}%</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <HardDrive className="mr-2 text-orange-500" size={20} /> Storage Resource Allocation
            </h3>
            {/* Placeholder for Storage Resource Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <LineChart size={30} className="mr-2" /> Storage Resource Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.storageResources.map((resource, index) => (
                <li key={index} className="flex justify-between">
                  <span>{resource.name}:</span>
                  <span className="font-medium text-gray-800">{resource.allocation} GB</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <Network className="mr-2 text-purple-500" size={20} /> Network Resource Allocation
            </h3>
            {/* Placeholder for Network Resource Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <LineChart size={30} className="mr-2" /> Network Resource Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.networkResources.map((resource, index) => (
                <li key={index} className="flex justify-between">
                  <span>{resource.name}:</span>
                  <span className="font-medium text-gray-800">{resource.allocation} Mbps</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Resource Allocations */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Target className="mr-2 text-green-600" size={24} /> Resource Allocations
        </h2>
        <div className="flex items-center space-x-4 mb-4">
          <select
            value={resourceFilter}
            onChange={(e) => setResourceFilter(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Resource Types</option>
            <option value="cpu">CPU Resources</option>
            <option value="memory">Memory Resources</option>
            <option value="storage">Storage Resources</option>
            <option value="network">Network Resources</option>
            <option value="database">Database Resources</option>
            <option value="cache">Cache Resources</option>
          </select>
          <div className="relative">
            <input
              type="text"
              placeholder="Search allocations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resource Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Allocation
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAllocations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No allocations matching criteria.</td>
                </tr>
              ) : (
                filteredAllocations.map((allocation) => (
                  <tr key={allocation.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{allocation.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{allocation.resourceType}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(allocation.status)}`}>
                        {allocation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{allocation.allocation}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(allocation.priority)}`}>
                        {allocation.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {allocation.status === 'available' && (
                        <button onClick={() => handleAllocateResource(allocation.id)} className="text-green-600 hover:text-green-900 mr-4">
                          <CheckCircle2 size={18} />
                        </button>
                      )}
                      {allocation.status === 'allocated' && (
                        <button onClick={() => handleDeallocateResource(allocation.id)} className="text-red-600 hover:text-red-900">
                          <XCircle size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Resource Pools */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Database className="mr-2 text-indigo-600" size={24} /> Resource Pools
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resourcePools.map((pool) => (
            <div key={pool.id} className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{pool.name}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(pool.status)}`}>
                  {pool.status}
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>CPU:</span>
                  <span className="font-medium">{pool.cpuAllocation}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Memory:</span>
                  <span className="font-medium">{pool.memoryAllocation}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Storage:</span>
                  <span className="font-medium">{pool.storageAllocation} GB</span>
                </div>
                <div className="flex justify-between">
                  <span>Network:</span>
                  <span className="font-medium">{pool.networkAllocation} Mbps</span>
                </div>
              </div>
              <div className="mt-4 flex space-x-2">
                <button className="flex-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700">
                  Configure
                </button>
                <button className="flex-1 px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700">
                  Monitor
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Resource Configuration */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Settings className="mr-2 text-gray-600" size={24} /> Resource Configuration
        </h2>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="autoAllocation" className="block text-sm font-medium text-gray-700">Auto Allocation</label>
              <input
                type="checkbox"
                id="autoAllocation"
                checked={resourceConfig.autoAllocation}
                onChange={(e) => setResourceConfig({ ...resourceConfig, autoAllocation: e.target.checked })}
                className="mt-1 form-checkbox"
              />
            </div>
            <div>
              <label htmlFor="allocationThreshold" className="block text-sm font-medium text-gray-700">Allocation Threshold (%)</label>
              <input
                type="number"
                id="allocationThreshold"
                value={resourceConfig.allocationThreshold}
                onChange={(e) => setResourceConfig({ ...resourceConfig, allocationThreshold: parseInt(e.target.value) })}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="monitoringInterval" className="block text-sm font-medium text-gray-700">Monitoring Interval (seconds)</label>
              <input
                type="number"
                id="monitoringInterval"
                value={resourceConfig.monitoringInterval}
                onChange={(e) => setResourceConfig({ ...resourceConfig, monitoringInterval: parseInt(e.target.value) })}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="alertEnabled" className="block text-sm font-medium text-gray-700">Alert Enabled</label>
              <input
                type="checkbox"
                id="alertEnabled"
                checked={resourceConfig.alertEnabled}
                onChange={(e) => setResourceConfig({ ...resourceConfig, alertEnabled: e.target.checked })}
                className="mt-1 form-checkbox"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="resourceTypes" className="block text-sm font-medium text-gray-700">Resource Types</label>
              <div className="mt-1 flex items-center space-x-4">
                {resourceConfig.resourceTypes.map((type) => (
                  <label key={type} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={resourceConfig.enabledTypes.includes(type)}
                      onChange={(e) => {
                        const enabledTypes = e.target.checked
                          ? [...resourceConfig.enabledTypes, type]
                          : resourceConfig.enabledTypes.filter(t => t !== type);
                        setResourceConfig({ ...resourceConfig, enabledTypes });
                      }}
                    />
                    <span className="ml-2 text-sm text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleUpdateResourceConfig}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <Save className="mr-2" size={18} /> Save Configuration
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourceManagement;
